/** Back office → Academy → Promo codes and sale settings (D-110). The engine is generic and off by
 * default; every amount is the agency's data (⚠️ to be confirmed by the owner). */

import {
  type AcademySettings,
  type PromoCodeKindEnum,
  useAcademySalesPromoCodesCreate,
  useAcademySalesPromoCodesList,
  useAcademySalesPromoCodesPartialUpdate,
  useAcademySettingsList,
  useAcademySettingsPartialUpdate,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, Checkbox, GlassCard, SelectField, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { dateText } from "../../billing/shared";
import { ErrorList, Pager, digits, isObject, num, useAcademyRoles, useRefreshAcademy } from "../shared";

function PromoForm() {
  const { t } = useTranslation();
  const refresh = useRefreshAcademy();
  const create = useAcademySalesPromoCodesCreate();
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<PromoCodeKindEnum>("percent");
  const [value, setValue] = useState("10");
  const [until, setUntil] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [first, setFirst] = useState(false);
  const [campaign, setCampaign] = useState("");
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        data: {
          code,
          kind,
          value: Number(value),
          valid_until: until || null,
          max_uses: maxUses ? Number(maxUses) : null,
          first_purchase_only: first,
          campaign,
        },
      },
      {
        onSuccess: () => {
          setCode("");
          refresh();
        },
      },
    );
  };
  return (
    <form className="stack" onSubmit={onSubmit}>
      <div className="row wrap">
        <TextField label={t("academySales.admin.promoCode")} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required />
        <SelectField
          label={t("academySales.admin.promoKind")}
          value={kind}
          onChange={(e) => setKind(e.target.value === "amount" ? "amount" : "percent")}
          options={[
            { value: "percent", label: t("academySales.admin.percent") },
            { value: "amount", label: t("academySales.admin.fixed") },
          ]}
        />
        <TextField label={t("academySales.admin.value")} inputMode="numeric" value={value} onChange={(e) => setValue(digits(e.target.value))} required />
        <TextField label={t("academySales.admin.until")} type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        <TextField label={t("academySales.admin.maxUses")} inputMode="numeric" value={maxUses} onChange={(e) => setMaxUses(digits(e.target.value))} />
        <TextField label={t("academySales.admin.campaign")} value={campaign} onChange={(e) => setCampaign(e.target.value)} />
      </div>
      <Checkbox label={t("academySales.admin.firstOnly")} checked={first} onChange={(e) => setFirst(e.target.checked)} />
      <ErrorList error={create.error} />
      <div>
        <Button type="submit" disabled={create.isPending || !code.trim()}>
          {t("academySales.admin.create")}
        </Button>
      </div>
    </form>
  );
}

export function PromoCodesTab() {
  const { t, i18n } = useTranslation();
  const { canEditSettings } = useAcademyRoles();
  const [page, setPage] = useState(1);
  const list = useAcademySalesPromoCodesList({ page });
  const toggle = useAcademySalesPromoCodesPartialUpdate();
  const refresh = useRefreshAcademy();
  const settings = useAcademySettingsList();
  const row = settings.data?.status === 200 ? settings.data.data.results[0] : undefined;
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const promoOn = isObject(row?.sales) && isObject(row.sales.promo) && Boolean(row.sales.promo.enabled);
  return (
    <div className="stack">
      {row ? <SalesSettings row={row} /> : <Skeleton style={{ height: 160 }} />}
      <GlassCard as="section" className="stack">
        <div className="section-title">
          <h2>{t("academySales.admin.tab.promos")}</h2>
        </div>
        {!promoOn ? (
          <p role="note" className="notice-warning">
            {t("academySales.admin.promoDisabled")}
          </p>
        ) : null}
        {canEditSettings ? <PromoForm /> : null}
        {data === undefined ? (
          <Skeleton style={{ height: 120 }} />
        ) : data.results.length === 0 ? (
          <p className="muted">{t("academySales.admin.empty")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("academySales.admin.promoCode")}</th>
                  <th scope="col">{t("academySales.admin.value")}</th>
                  <th scope="col">{t("academySales.admin.until")}</th>
                  <th scope="col">{t("academySales.admin.uses")}</th>
                  <th scope="col">{t("academySales.admin.active")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((promo) => (
                  <tr key={promo.id}>
                    <td>
                      <strong className="ao-num">{promo.code}</strong>
                      {promo.campaign ? <div className="muted">{promo.campaign}</div> : null}
                      {promo.first_purchase_only ? <Badge>{t("academySales.admin.firstOnly")}</Badge> : null}
                    </td>
                    <td className="ao-num">{promo.kind === "percent" ? `${promo.value} %` : formatXaf(promo.value)}</td>
                    <td>{promo.valid_until ? dateText(promo.valid_until, i18n.language) : "—"}</td>
                    <td className="ao-num">
                      {promo.uses}
                      {promo.max_uses ? ` / ${promo.max_uses}` : ""}
                    </td>
                    <td>
                      <SoftToggle
                        label={t("academySales.admin.active")}
                        checked={Boolean(promo.is_active)}
                        disabled={!canEditSettings}
                        onChange={(value) => toggle.mutate({ id: promo.id, data: { is_active: value } }, { onSuccess: refresh })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data ? <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} /> : null}
      </GlassCard>
    </div>
  );
}

/** The sale settings of the agency (data, D-110): trial, promo codes, referral, refund period,
 * reminders, simulator, payment methods by country, purchase from the mobile apps. */
function SalesSettings({ row }: { row: AcademySettings }) {
  const { t } = useTranslation();
  const { canEditSettings } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const save = useAcademySettingsPartialUpdate();
  const sales = isObject(row.sales) ? row.sales : {};
  const trial = isObject(sales.trial) ? sales.trial : {};
  const discovery = isObject(trial.discovery) ? trial.discovery : {};
  const referral = isObject(sales.referral) ? sales.referral : {};
  const reminders = isObject(sales.reminders) ? sales.reminders : {};
  const mobile = isObject(sales.mobile_purchase) ? sales.mobile_purchase : {};
  const [trialDays, setTrialDays] = useState(String(num(trial.days)));
  const [discoveryOn, setDiscoveryOn] = useState(discovery.enabled !== false);
  const [questions, setQuestions] = useState(String(num(discovery.questions) || 10));
  const [promoOn, setPromoOn] = useState(isObject(sales.promo) && Boolean(sales.promo.enabled));
  const [referralOn, setReferralOn] = useState(Boolean(referral.enabled));
  const [refereePercent, setRefereePercent] = useState(String(num(referral.referee_discount_percent)));
  const [referrerDays, setReferrerDays] = useState(String(num(referral.referrer_free_days)));
  const [refundDays, setRefundDays] = useState(String(isObject(sales.refund) ? num(sales.refund.days) : 14));
  const [steps, setSteps] = useState((Array.isArray(reminders.days_before) ? reminders.days_before : [3, 1, 0]).join(", "));
  const [simulator, setSimulator] = useState(sales.simulator !== false);
  const [routing, setRouting] = useState(JSON.stringify(sales.routing ?? {}, null, 2));
  const [ios, setIos] = useState(mobile.ios === "web_link" ? "web_link" : "none");
  const [android, setAndroid] = useState(mobile.android === "none" ? "none" : "web_link");
  const [routingError, setRoutingError] = useState("");

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    let parsedRouting: unknown;
    try {
      parsedRouting = JSON.parse(routing);
    } catch {
      setRoutingError(t("common.error"));
      return;
    }
    setRoutingError("");
    save.mutate(
      {
        id: row.id,
        data: {
          sales: {
            ...sales,
            trial: { ...trial, days: Number(trialDays) || 0, discovery: { ...discovery, enabled: discoveryOn, questions: Number(questions) || 10 } },
            promo: { enabled: promoOn },
            referral: { enabled: referralOn, referee_discount_percent: Number(refereePercent) || 0, referrer_free_days: Number(referrerDays) || 0 },
            refund: { days: Number(refundDays) || 0 },
            reminders: {
              ...reminders,
              days_before: steps
                .split(/[,;\s]+/)
                .filter(Boolean)
                .map(Number)
                .filter((n) => Number.isInteger(n)),
            },
            simulator,
            routing: parsedRouting,
            mobile_purchase: { ios, android },
          },
        },
      },
      { onSuccess: refresh },
    );
  };

  return (
    <GlassCard as="section" className="stack">
      <form className="stack" onSubmit={onSubmit}>
        <div className="section-title">
          <div>
            <h2>{t("academySales.admin.settings")}</h2>
            <p className="muted">{t("academySales.admin.settingsHint")}</p>
          </div>
        </div>
        <fieldset className="stack" disabled={!canEditSettings}>
          <div className="row wrap">
            <TextField label={t("academySales.admin.trialDays")} inputMode="numeric" value={trialDays} onChange={(e) => setTrialDays(digits(e.target.value))} />
            <TextField label={t("academySales.admin.discoveryQuestions")} inputMode="numeric" value={questions} onChange={(e) => setQuestions(digits(e.target.value))} />
            <TextField label={t("academySales.admin.refundDays")} inputMode="numeric" value={refundDays} onChange={(e) => setRefundDays(digits(e.target.value))} />
            <TextField label={t("academySales.admin.reminders")} value={steps} onChange={(e) => setSteps(e.target.value)} />
          </div>
          <SoftToggle label={t("academySales.admin.discovery")} checked={discoveryOn} onChange={setDiscoveryOn} />
          <SoftToggle label={t("academySales.admin.promoEnabled")} checked={promoOn} onChange={setPromoOn} />
          <SoftToggle label={t("academySales.admin.referralEnabled")} checked={referralOn} onChange={setReferralOn} />
          {referralOn ? (
            <div className="row wrap">
              <TextField label={t("academySales.admin.refereePercent")} inputMode="numeric" value={refereePercent} onChange={(e) => setRefereePercent(digits(e.target.value))} />
              <TextField label={t("academySales.admin.referrerDays")} inputMode="numeric" value={referrerDays} onChange={(e) => setReferrerDays(digits(e.target.value))} />
            </div>
          ) : null}
          <SoftToggle label={t("academySales.admin.simulator")} checked={simulator} onChange={setSimulator} />
          <div className="row wrap">
            <SelectField
              label={t("academySales.admin.androidPurchase")}
              value={android}
              onChange={(e) => setAndroid(e.target.value)}
              options={[
                { value: "web_link", label: t("academySales.admin.webLink") },
                { value: "none", label: t("academySales.admin.none") },
              ]}
            />
            <SelectField
              label={t("academySales.admin.iosPurchase")}
              value={ios}
              onChange={(e) => setIos(e.target.value)}
              options={[
                { value: "none", label: t("academySales.admin.none") },
                { value: "web_link", label: t("academySales.admin.webLink") },
              ]}
            />
          </div>
          <TextAreaField
            label={t("academySales.admin.routing")}
            hint={t("academySales.admin.routingHint")}
            rows={8}
            value={routing}
            onChange={(e) => setRouting(e.target.value)}
            error={routingError || undefined}
            className="ao-mono"
          />
        </fieldset>
        <ErrorList error={save.error} />
        {save.isSuccess ? (
          <p role="status" className="ok">
            {t("academySales.admin.saved")}
          </p>
        ) : null}
        {canEditSettings ? (
          <div>
            <Button type="submit" disabled={save.isPending}>
              {t("academySales.account.save")}
            </Button>
          </div>
        ) : null}
      </form>
    </GlassCard>
  );
}
